import React from 'react';
import { doc, setDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { FaUserShield, FaUserLock, FaCrown, FaUser, FaUserSecret } from 'react-icons/fa';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useNavigate } from 'react-router-dom';

const AdminDashboardUserSettingModal = ({ isOpen, onClose, user }) => {
    const { startImpersonation } = useImpersonation();
    const navigate = useNavigate();
    
    if (!isOpen || !user) return null;

    const handleUserStatusUpdate = async (action) => {
        try {
            const userRef = doc(db, 'users', user.id);
            
            if (action === 'suspend') {
                console.log('Suspending user:', user.id); // Debug log
                
                const updateData = {
                    status: 'suspended',
                    approved: false,
                    suspendedAt: new Date().toISOString(),
                    // If they were premium, downgrade them
                    ...(user.membershipLevel === 'premium' && { membershipLevel: 'free' })
                };
                
                console.log('Update data:', updateData); // Debug log
                
                await setDoc(userRef, updateData, { merge: true });
                console.log('User suspended successfully'); // Debug log
                
                toast.success(user.membershipLevel === 'premium' 
                    ? 'User has been suspended. Remember to cancel their Stripe subscription manually.' 
                    : 'User has been suspended');
            } else if (action === 'activate') {
                await setDoc(userRef, {
                    status: 'approved',
                    approved: true
                }, { merge: true });
                toast.success('User has been activated');
            }
            
            onClose();
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error('Failed to update user status');
        }
    };

    const handleMembershipUpdate = async (newMembership) => {
        try {
            const userRef = doc(db, 'users', user.id);
            await setDoc(userRef, {
                membershipLevel: newMembership
            }, { merge: true });

            // Create notification for the user
            const notificationsRef = collection(db, 'notifications');
            await addDoc(notificationsRef, {
                userId: user.id,
                message: `Your membership has been ${newMembership === 'free' ? 'downgraded to Free Tier' : 'upgraded to Promo Premium'}. View your profile to see your latest plan details.${user.membershipLevel === 'premium' && newMembership === 'free' ? ' If you had an active subscription, it has been cancelled.' : ''}`,
                createdAt: new Date(),
                read: false,
                type: 'membership'
            });

            toast.success(`User membership updated to ${newMembership}${user.membershipLevel === 'premium' && newMembership === 'free' ? '. Remember to cancel their Stripe subscription manually.' : ''}`);
            onClose();
        } catch (error) {
            console.error('Error updating membership:', error);
            toast.error('Failed to update membership');
        }
    };

    const handleImpersonation = () => {
        startImpersonation(user);
        toast.success(`Now impersonating ${user.username}`);
        onClose();
        navigate('/dashboard');
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-lg bg-white">
                {/* Header */}
                <div className="border-b pb-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900">
                            User Settings
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <span className="sr-only">Close</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                        Managing settings for {user.username}
                    </p>
                </div>

                {/* User Info */}
                <div className="py-4 space-y-4">
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <FaUserShield className="h-5 w-5 text-gray-400" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">Account Status</p>
                                <p className={`text-sm ${user.status === 'suspended' ? 'text-red-600' : 'text-green-600'}`}>
                                    {user.status || 'Approved'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                {user.membershipLevel === 'premium' ? (
                                    <FaCrown className="h-5 w-5 text-yellow-500" />
                                ) : user.membershipLevel === 'promo' ? (
                                    <FaCrown className="h-5 w-5 text-purple-500" />
                                ) : (
                                    <FaUser className="h-5 w-5 text-gray-400" />
                                )}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">Membership Level</p>
                                <p className="text-sm text-gray-600">
                                    {user.membershipLevel === null || user.membershipLevel === undefined 
                                        ? 'Not Set' 
                                        : user.membershipLevel.charAt(0).toUpperCase() + user.membershipLevel.slice(1)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="space-y-4 mt-4">
                    {/* Membership Management */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Membership Management</h4>
                        <div className="space-y-2">
                            {user.membershipLevel === null || user.membershipLevel === undefined ? (
                                <button
                                    onClick={() => handleMembershipUpdate('free')}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    <FaUser className="mr-2 h-4 w-4" />
                                    Set to Free
                                </button>
                            ) : user.membershipLevel === 'free' ? (
                                <button
                                    onClick={() => handleMembershipUpdate('promo')}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                >
                                    <FaCrown className="mr-2 h-4 w-4" />
                                    Upgrade to Promo
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleMembershipUpdate('free')}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    <FaUser className="mr-2 h-4 w-4" />
                                    Downgrade to Free
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Status Management */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Status Management</h4>
                        {user.status !== 'suspended' ? (
                            <button
                                onClick={() => handleUserStatusUpdate('suspend')}
                                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <FaUserLock className="mr-2 h-4 w-4" />
                                Suspend User
                            </button>
                        ) : (
                            <button
                                onClick={() => handleUserStatusUpdate('activate')}
                                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                                <FaUserShield className="mr-2 h-4 w-4" />
                                Activate User
                            </button>
                        )}
                    </div>

                    {/* Impersonation */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Actions</h4>
                        <button
                            onClick={handleImpersonation}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <FaUserSecret className="mr-2 h-4 w-4" />
                            Impersonate User
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardUserSettingModal;
